import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import CommonRequestTrialButtonComponent from 'webapp/components/common/request-trial-button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';
import WizardSourceMetricStepsComponent from 'webapp/components/wizard/source-metric-steps';

import BillingPlan from 'webapp/models-custom/billing-plan';
import type Source from 'webapp/models-custom/source';

import type { AfterConnectFn } from 'webapp/services/connect';
import { StepId } from 'webapp/services/wizard';

type Args = Record<string, never>;

export default class WizardStartingComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('logger') declare loggerService: Service.Logger;

	trialFeature = BillingFeature.QB;
	trackingOrigin = 'wizard';

	get selectedSourceType() {
		return this.wizardService.selectedSourceType;
	}

	get isCustom() {
		return this.selectedSourceType?.key === 'Custom';
	}

	get notAllowed() {
		return (
			this.selectedSourceType?.isPremium && !this.billingService.checkIfCan(this.trialFeature)
		);
	}

	get premiumSourceImage() {
		switch (this.selectedSourceType?.key) {
			case 'GoogleSheets':
				return 'gsheets';
			case 'Jira':
				return 'jira';
			case 'PostgreSQL':
			case 'MySQL':
			case 'BigQuery':
			case 'AmazonRedshift':
			case 'AzureSQL':
			case 'MSSQL':
			case 'Snowflake':
				return 'sql';
			default: // ElasticSearch, Teamwork
				return 'generic';
		}
	}

	get readMoreUrl() {
		return this.selectedSourceType?.genericGuideLink;
	}

	get showTrialLink() {
		return this.billingService.canRequestTrial;
	}

	get showHelp() {
		return !this.accountService.hideHelpArticles;
	}

	get targetPlan() {
		return this.billingService.getNextPlanForFeatureWithSameBillingCycle(this.trialFeature);
	}

	get targetPlanType() {
		return this.targetPlan?.planType || '';
	}

	get targetPlanText() {
		return this.isPlanForTrialOverriden
			? 'paid plans'
			: `${capitalize(this.targetPlanType)} ${this.isTargetPlanNameHighest ? ' plan' : (this.targetPlanType ? ' and' : '') + ' higher plans'}`;
	}

	get isTargetPlanNameHighest() {
		const planTypes = BillingPlan.planTypes;
		return planTypes[planTypes.length - 1] === this.targetPlanType;
	}

	get isPlanForTrialOverriden() {
		return this.billingService.checkIfPlanForTrialIsOverriden(this.targetPlanType);
	}

	@action
	back() {
		this.wizardService.goToPrevious();
	}

	@action
	connectSource(sourceKey: string, afterConnect: AfterConnectFn) {
		this.wizardService.connectSource(sourceKey, afterConnect);
	}

	@action
	createNewDataboard(source: Source | null) {
		if (source) {
			this.wizardService.createNewDataboard(source);
		}
	}

	@action
	changeSource() {
		this.wizardService.goToSources();
	}

	@action
	viewMetricLibrary(source: Source | null) {
		if (source) {
			this.navigationService.transitionToRoute(
				'metric-library.source.specific',
				source.sourceType.key,
				source.id,
			);
		} else {
			this.loggerService.logError('[ONBOARDING] Custom token flow - source not exist');
			this.navigationService.transitionToRoute('metric-library.index');
		}
	}

	@action
	checkRedirect() {
		// when premium source without templates and metrics gets trial-enabled,
		// redirect to /connect
		if (!this.notAllowed && this.selectedSourceType?.isQbOnly) {
			this.wizardService.goToStep(StepId.CONNECT);
		}
	}
	<template>
		{{#unless this.notAllowed}}
			<div class="wizard-title">
				<div class="h">
					{{#if this.isCustom}}
						<h2>Welcome developers!</h2>
						<p>
							{{#if this.accountService.whiteLabelingEnabled}}
								Here
							{{else}}
								With Databox,
							{{/if}}
							you can build powerful reports in a snap.
						</p>
					{{/if}}
				</div>

				<div role="button" class="source" {{on "click" this.changeSource}}>
					<div>
						<span class="s">Change data source</span>
						<img
							src={{this.selectedSourceType.avatar}}
							alt={{this.selectedSourceType.name}}
						/>
						<span>{{this.selectedSourceType.name}}</span>
					</div>
				</div>
			</div>
		{{/unless}}

		<div
			class="content {{if this.notAllowed 'content--qb-trial'}}"
			{{didUpdate this.checkRedirect this.notAllowed}}
		>

			{{#if this.notAllowed}}

				<div class="not-allowed">

					<div class="box">
						<div class="text">
							<img
								src={{this.selectedSourceType.avatar}}
								alt={{this.selectedSourceType.name}}
							/>
							<div>
								<strong>{{this.selectedSourceType.name}}</strong>
								<span>Custom metric support</span>
							</div>
							<p>{{this.selectedSourceType.description}}</p>
							{{#if this.showHelp}}
								<a href={{this.readMoreUrl}} target="_blank" class="read-more">
									Learn more
									<CommonUiIconComponent @name="External" @size="sm" />
								</a>
							{{/if}}
						</div>
						<div class="img">
							<img
								src="/images/onboarding/premium-source-{{this.premiumSourceImage}}.png"
								width="325"
								height="216"
								alt={{this.selectedSourceType.name}}
							/>
						</div>
					</div>

					<div class="action">

						<p>
							You're trying to connect
							{{this.selectedSourceType.name}}, an integration that requires the use
							of
							{{#if this.showHelp}}
								<a
									href="https://help.databox.com/create-a-custom-metric"
									target="_blank"
								>
									Metric builder
								</a>
							{{else}}
								Metric builder
							{{/if}}, which is only available on
							{{this.targetPlanText}}.
							{{#if this.showTrialLink}}
								{{#if this.billingService.isActiveTrial}}
									Change trial plan
								{{else}}
									Start a free 14-day trial period
								{{/if}}
								to connect this integration.
							{{/if}}
						</p>

						<div class="buttons">

							{{#if this.showTrialLink}}
								<div class="btn-trial">
									<CommonRequestTrialButtonComponent
										@feature={{this.trialFeature}}
										@size="big"
										@trackingOrigin={{this.trackingOrigin}}
									/>
								</div>
							{{else}}
								<CommonUpgradePlanButtonComponent
									@feature={{this.trialFeature}}
									@size="big"
									@icon="ArrowUpCircleFill"
									@theme="grey-outlined"
									@trackingOrigin={{this.trackingOrigin}}
								/>
							{{/if}}

						</div>

					</div>

				</div>

			{{else}}

				<div class="list">

					<WizardSourceMetricStepsComponent
						@sourceType={{this.selectedSourceType}}
						@onConnectSource={{this.connectSource}}
						@onCreateNewDataboard={{this.createNewDataboard}}
						@onViewMetricLibrary={{this.viewMetricLibrary}}
					/>

				</div>

			{{/if}}

			<div class="wizard-controls smart-center-pos">
				<div class="container">
					<a role="button" class="btn back" {{on "click" this.back}}>
						<CommonUiIconComponent @name="ArrowLeft" @size="sm" />
						Go back
					</a>
				</div>
			</div>
		</div>
	</template>
}
